import { useEffect, useSyncExternalStore } from 'react';
import { ENTITY_PREFIX, type EntityKind } from '../utils/entityRoutes';
import { parseId } from '../utils/jiraId';

// The ids a list screen is showing, in the order and with the filters the user
// sees, so a record page can step to the previous / next row of THAT list
// (see useRecordNav). List screens write here from where they already compute
// their rows; nothing here fetches data.

const STORAGE_PREFIX = 'ans:record-list:';

const lists = new Map<EntityKind, readonly string[]>();
const listeners = new Set<() => void>();

/** One id in the form the lists and the URLs agree on: no prefix, no zero padding. */
export function normalizeRecordId(kind: EntityKind, id: string | number | null | undefined): string | undefined {
  if (id === null || id === undefined || id === '') return undefined;
  return parseId(ENTITY_PREFIX[kind], String(id));
}

function readStored(kind: EntityKind): readonly string[] | undefined {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + kind);
    const parsed: unknown = raw ? JSON.parse(raw) : undefined;
    return Array.isArray(parsed) && parsed.every(v => typeof v === 'string') ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function writeStored(kind: EntityKind, ids: readonly string[]): void {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + kind, JSON.stringify(ids));
  } catch {
    // Private mode / quota: the in-memory list still works for this page load.
  }
}

/** The list for a kind: what the last list screen published, else what this tab stored before a reload. */
export function getRecordList(kind: EntityKind): readonly string[] {
  const live = lists.get(kind);
  if (live) return live;
  const stored = readStored(kind);
  if (stored) lists.set(kind, stored);
  return stored ?? EMPTY;
}

const EMPTY: readonly string[] = Object.freeze([]);

/** Publishes the visible ids of a list. Ids may carry their prefix; duplicates and blanks are dropped. */
export function publishRecordList(kind: EntityKind, ids: ReadonlyArray<string | number | null | undefined>): void {
  const seen = new Set<string>();
  const next: string[] = [];
  for (const id of ids) {
    const norm = normalizeRecordId(kind, id);
    if (norm !== undefined && !seen.has(norm)) {
      seen.add(norm);
      next.push(norm);
    }
  }
  const prev = getRecordList(kind);
  if (prev.length === next.length && prev.every((v, i) => v === next[i])) return;
  lists.set(kind, Object.freeze(next));
  writeStored(kind, next);
  listeners.forEach(l => l());
}

/** Test helper: forget every list. */
export function resetRecordLists(): void {
  lists.clear();
  listeners.forEach(l => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The published list of a kind, re-rendering when a list screen publishes a new one. */
export function useRecordList(kind: EntityKind): readonly string[] {
  return useSyncExternalStore(subscribe, () => getRecordList(kind), () => EMPTY);
}

/**
 * For a list screen: publish `ids` (in display order) whenever they change.
 * Unchanged lists are ignored; `undefined` kind publishes nothing (for components where the list is optional).
 */
export function usePublishRecordList(
  kind: EntityKind | undefined,
  ids: ReadonlyArray<string | number | null | undefined>,
): void {
  const key = ids.join('\u0001');
  useEffect(() => {
    if (kind) publishRecordList(kind, ids);
    // `key` stands for the contents of `ids`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, key]);
}
